import React, { useState, useEffect } from "react";
import { ListGroup } from "react-bootstrap";
import styles from "./search-reservation.module.css";
import useGetAllBooking from "../../../services/create-booking-v4/hooks/useGetAllBookingV4";
import FormSpinner from "../spinner/FormSpinner";
import { useAuth } from "../../../auth/context/AuthProvider";

interface SearchReservationsProps {
    onDetailClick: (reservationId: string) => void;
}

const SearchReservations: React.FC<SearchReservationsProps> = ({
    onDetailClick,
}) => {
    const { user } = useAuth();
    const [searchQuery, setSearchQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(-1);

    const { data: reservations, isFetching, isError, isSuccess } = useGetAllBooking({
        filters: {
            property_id: user?.active_property_id,
        },
        search: searchQuery,
        limit: 3,
        queryKey: "searchbar",
    });

    const results = reservations?.data || [];

    useEffect(() => {
        // Reset index when user types something new or results change
        setActiveIndex(-1);
    }, [searchQuery]);

    const handleClick = (reservationId: string) => {
        setSearchQuery("");
        setActiveIndex(-1);
        onDetailClick(reservationId);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (results.length === 0) return;

        if (e.key === "ArrowDown") {
            e.preventDefault(); // Prevent cursor moving to end of input
            setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        } else if (e.key === "ArrowUp") {
            e.preventDefault(); // Prevent cursor moving to start of input
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
        } else if (e.key === "Enter") {
            if (activeIndex >= 0 && activeIndex < results.length) {
                handleClick(results[activeIndex].id);
            }
        } else if (e.key === "Escape") {
            setSearchQuery("");
            setActiveIndex(-1);
            // Remove focus from input so global shortcuts work again
            (e.currentTarget as HTMLInputElement).blur();
        }
    };

    return (
        <>
            <input
                id="global-search-input"
                type="text"
                placeholder="Search reservation"
                className="form-control me-2 d-none d-sm-block"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={results.length > 0}
                aria-haspopup="listbox"
                aria-controls="search-results-list"
                aria-activedescendant={activeIndex >= 0 ? `result-item-${results[activeIndex].id}` : undefined}
            />

            {searchQuery && !isSuccess && !isFetching && isError && (
                <div className={styles.searchResults}>
                    <ListGroup>
                        <ListGroup.Item className={styles.listItem}>
                            <span className="text-muted text-center">Failed to fetch data</span>
                        </ListGroup.Item>
                    </ListGroup>
                </div>
            )}

            {searchQuery && isFetching && (
                <div className={styles.searchResults}>
                    <ListGroup>
                        <ListGroup.Item className={styles.listItem}>
                            <div className="d-flex justify-content-center w-100">
                                <FormSpinner />
                            </div>
                        </ListGroup.Item>
                    </ListGroup>
                </div>
            )}

            {searchQuery && isSuccess && !isFetching && results.length === 0 && (
                <div className={styles.searchResults}>
                    <ListGroup>
                        <ListGroup.Item className={styles.listItem}>
                            <span className="text-muted text-center">No results found</span>
                        </ListGroup.Item>
                    </ListGroup>
                </div>
            )}

            {searchQuery && isSuccess && !isFetching && results.length > 0 && (
                <div className={styles.searchResults}>
                    <ListGroup id="search-results-list" role="listbox">
                        {results.map((result, index) => (
                            <ListGroup.Item
                                key={result.id}
                                id={`result-item-${result.id}`}
                                role="option"
                                aria-selected={activeIndex === index}
                                className={`${styles.listItem} ${activeIndex === index ? styles.activeItem : ""}`}
                                onClick={() => handleClick(result.id)}
                            >
                                <div>
                                    <small>Reservation ID {result.invoice_number}</small><br />
                                    <strong>{result.customer_name}</strong><br />
                                    <small key={result.room?.id}>{result.room_type?.name} : {result.room?.room_number}</small>
                                </div>
                                <div>
                                    <small>{result.checkin_date} - {result.checkout_date}</small><br />
                                    <span className="badge bg-primary text-light">
                                        {result.status}
                                    </span>
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </div>
            )}
        </>
    );
};

export default SearchReservations;